import { getIntl } from 'umi';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import {
  analysisDataSourceMetricKey,
  generateDataSourceMetricKey,
  getOptionText,
} from '@intelligence/utils/topchartsUtils';
import { isEmpty } from '@intelligence/v3/utils/common';
import { SortMode, SortModes, SortType } from '@home/enums/common';
import {
  COMBINE_DATA_SOURCES_MAPPING,
  COMBINE_DATA_SOURCES_METRICS_MAPPING,
} from '@intelligence/v3/_config/metrics/combine';

export const getDataSourceName = (locale: string, dataSource: string, dataSourceName?: string): string => {
  if (!isEmpty(dataSourceName)) return dataSourceName!;
  const dataSourceData = COMBINE_DATA_SOURCES_MAPPING[dataSource] ?? {};
  const dataSourceNameData = dataSourceData?.dataSourceName;
  // @ts-ignore
  return getOptionText(locale, { textEN: dataSourceNameData?.en, textCN: dataSourceNameData?.cn });
};

export const getDataSourceIconInComp = (dataSource: string, dataSourceIcon?: string): string => {
  if (!isEmpty(dataSourceIcon)) return dataSourceIcon!;
  return getDataSourceIcon(dataSource);
};

export const getMetricName = (locale: string, dataSource: string, metric: string, metricName?: string): string => {
  if (!isEmpty(metricName)) return metricName!;
  const metricData = COMBINE_DATA_SOURCES_METRICS_MAPPING[dataSource]?.[metric] ?? {};
  const metricNameData = metricData?.metricName;
  // @ts-ignore
  return getOptionText(locale, { textEN: metricNameData?.en, textCN: metricNameData?.cn });
};

const formatSortModeOptionField = (sortMode?: SortMode) => {
  return isEmpty(sortMode) ? String(SortMode.Number) : String(sortMode);
};

export const parseSortModeOptionField = (sortMode?: string) => {
  if (isEmpty(sortMode)) return SortMode.Number;
  const sortModeNum = Number.parseInt(sortMode!, 10);
  return SortModes.includes(sortModeNum) ? sortModeNum : SortMode.Number;
};

export const formatOptions = (metricName: string, useRatioSort?: boolean): ISortOption[] => {
  if (!metricName) return [];
  const intl = getIntl();
  const results = [
    {
      field: formatSortModeOptionField(SortMode.Number),
      text: metricName,
    },
  ];
  if (!!useRatioSort) {
    results.push({
      field: formatSortModeOptionField(SortMode.Ratio),
      text: `${metricName}:${intl.formatMessage({ id: 'dbi_change' })}(%)`,
    });
  }
  return results;
};

export const formatSorts = <T = any>(sorts?: IRatioMetricSortState<T>[] | null): ISortState<T>[] => {
  if (!Array.isArray(sorts) || sorts.length === 0) return [];

  return sorts.map((item) => {
    const dataSource = item?.dataSource ?? '';
    const metric = item?.metric ?? '';
    const type = item?.type ?? SortType.None;
    const mode = item?.mode ?? SortMode.Number;
    const comparator = item?.comparator;
    const field = generateDataSourceMetricKey(dataSource, metric);
    return {
      field,
      type,
      option: { field: formatSortModeOptionField(mode) },
      comparator,
    };
  });
};

export const parseSorts = <T = any>(sorts?: ISortState<T>[] | null): IRatioMetricSortState<T>[] => {
  if (!Array.isArray(sorts) || sorts.length === 0) return [];

  return sorts.map<IRatioMetricSortState>((item) => {
    const field = item?.field;
    const type = item?.type;
    const comparator = item?.comparator;
    const option = item?.option;
    const optionField = option?.field;
    const { dataSource, metric } = analysisDataSourceMetricKey(field);
    return {
      dataSource,
      metric,
      type,
      mode: parseSortModeOptionField(optionField),
      comparator,
    };
  });
};
